import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, ArrowLeft, Users, LogOut, Loader2, Pencil, Trash2, MessageCircle, CheckCheck, Search, Upload, BellRing, MessageSquareText } from "lucide-react";
import { API, EVENTS, apiError, waLink, smsLink } from "@/lib/reg";
import { PinGate } from "./PinGate";
import { EditDialog } from "./EditDialog";
import { BulkUpload } from "./BulkUpload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const evShort = (ids) => ids.map((id) => EVENTS.find((e) => e.id === id)?.en || id).join(", ");
const TOKEN_KEY = "gpm_org_token";

export default function DataPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [rows, setRows] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); setRows(null); };
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [q, setQ] = useState("");
  const [msgKind, setMsgKind] = useState("registration");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () =>
    axios.get(`${API}/registrations`, auth).then((r) => { setRows(r.data); setUpdatedAt(new Date()); }).catch((e) => {
      if (e?.response?.status === 401) logout();
      else setRows((p) => p || []);
    });

  useEffect(() => {
    if (!token) return;
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sendWa = (r) => {
    window.open(waLink(r, msgKind), "_blank", "noopener");
    markSent(r, msgKind === "reminder" ? "reminder" : "registration");
  };
  const sendSms = (r) => {
    window.location.href = smsLink(r, msgKind);
    markSent(r, msgKind === "reminder" ? "sms_reminder" : "sms");
  };
  const FIELD = { registration: "whatsapp_sent_at", reminder: "reminder_sent_at", sms: "sms_sent_at", sms_reminder: "sms_reminder_sent_at" };
  const markSent = (r, kind) => {
    const field = FIELD[kind];
    axios.post(`${API}/registrations/${r.registration_id}/whatsapp-sent?kind=${kind}`, {}, auth)
      .then(({ data }) => setRows((p) => p.map((x) => (x.registration_id === r.registration_id ? { ...x, [field]: data[field] } : x))))
      .catch(() => {});
  };
  const sentField = msgKind === "reminder" ? "reminder_sent_at" : "whatsapp_sent_at";
  const smsField = msgKind === "reminder" ? "sms_reminder_sent_at" : "sms_sent_at";
  const needle = q.trim().toLowerCase();
  const shown = (rows || []).filter((r) => !needle ||
    [r.full_name, r.father_name, r.mobile, r.registration_id, r.school].some((v) => (v || "").toLowerCase().includes(needle)));

  const remove = async () => {
    const r = deleting;
    try {
      await axios.delete(`${API}/registrations/${r.registration_id}`, auth);
      setRows((p) => p.filter((x) => x.registration_id !== r.registration_id));
      toast.success("रिकॉर्ड हटा दिया गया / Record deleted");
    } catch (e) {
      if (e?.response?.status === 401) logout();
      toast.error(apiError(e));
    }
    setDeleting(null);
  };

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/registrations/export`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "GPM_Sports_Meet_2026_Registrations.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      if (e?.response?.status === 401) logout();
      toast.error("डाउनलोड नहीं हो पाया / Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (!token) {
    return (
      <div className="px-4 py-16" data-testid="data-page">
        <PinGate onToken={setToken} />
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="data-page">
      <div className="flex justify-between">
        <a href="/" className="inline-flex items-center gap-2 text-white/70 text-sm" data-testid="data-back-link">
          <ArrowLeft size={16} /> होम पेज · Home
        </a>
        <button onClick={logout} className="inline-flex items-center gap-2 text-white/70 text-sm" data-testid="data-logout-btn">
          <LogOut size={16} /> लॉक करें · Lock
        </button>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">पंजीकृत खिलाड़ी · Registered Players</p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-bold text-white font-hi flex items-center gap-3">
            <Users className="text-orange-400" /> <span data-testid="data-count">{rows ? rows.length : "…"}</span> खिलाड़ी
          </h1>
          <p className="mt-2 inline-flex items-center gap-2 text-xs text-white/60" data-testid="live-indicator">
            <span className="live-dot" /> LIVE · हर 10 सेकंड में अपडेट{updatedAt && ` · ${updatedAt.toLocaleTimeString("en-IN")}`}
          </p>
          {rows && (
            <p className="mt-1 text-xs text-green-400 font-semibold" data-testid="wa-sent-count">
              पंजीकरण संदेश भेजे: {rows.filter((r) => r.whatsapp_sent_at).length} / {rows.length} · रिमाइंडर भेजे: {rows.filter((r) => r.reminder_sent_at).length} / {rows.length}
            </p>
          )}
          {rows && (
            <p className="mt-1 text-xs text-sky-300 font-semibold" data-testid="sms-sent-count">
              SMS पंजीकरण: {rows.filter((r) => r.sms_sent_at).length} / {rows.length} · SMS रिमाइंडर: {rows.filter((r) => r.sms_reminder_sent_at).length} / {rows.length}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setBulkOpen(true)} className="btn-outline !bg-transparent !text-white !border-white/60" data-testid="bulk-upload-btn">
            <Upload size={18} /> एक साथ जोड़ें · Bulk Add
          </button>
          <button onClick={download} disabled={downloading} className="btn-saffron" data-testid="download-excel-btn">
            {downloading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} Excel डाउनलोड करें
          </button>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-3">
        <label className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="नाम, पिता, मोबाइल, Reg. ID या स्कूल से खोजें · Search"
            className="gp-input !pl-11" data-testid="search-input" />
        </label>
        <div className="msg-toggle" data-testid="msg-kind-toggle">
          <button onClick={() => setMsgKind("registration")} className={msgKind === "registration" ? "on" : ""} data-testid="msg-kind-registration">
            <MessageCircle size={16} /> पंजीकरण संदेश
          </button>
          <button onClick={() => setMsgKind("reminder")} className={msgKind === "reminder" ? "on" : ""} data-testid="msg-kind-reminder">
            <BellRing size={16} /> रिमाइंडर संदेश
          </button>
        </div>
      </div>
      {needle && <p className="text-white/60 text-sm -mt-2" data-testid="search-result-count">{shown.length} मिले · found</p>}
      <div className="form-card overflow-auto max-h-[70vh]">
        <table className="data-table w-full text-sm" data-testid="data-table">
          <thead>
            <tr>{["", "#", "Reg. ID", "Name", "Father's Name", "Mobile", "Parent", "DOB", "Age", "Class", "School", "Events", "Emergency"].map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.registration_id} data-testid={`data-row-${r.registration_id}`}>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => sendWa(r)} className={`row-btn ${msgKind === "reminder" ? "remind" : "wa"} ${r[sentField] ? "sent" : ""}`}
                      title={r[sentField] ? "Sent — send again" : msgKind === "reminder" ? "Send reminder" : "Send WhatsApp"} data-testid={`wa-send-btn-${r.registration_id}`}>
                      {r[sentField] ? <CheckCheck size={15} /> : msgKind === "reminder" ? <BellRing size={15} /> : <MessageCircle size={15} />}
                    </button>
                    <button onClick={() => sendSms(r)} className={`row-btn sms ${r[smsField] ? "sent" : ""}`}
                      title={r[smsField] ? "SMS sent — send again" : "Send SMS"} data-testid={`sms-send-btn-${r.registration_id}`}>
                      {r[smsField] ? <CheckCheck size={15} /> : <MessageSquareText size={15} />}
                    </button>
                    <button onClick={() => setEditing(r)} className="row-btn" title="Edit" data-testid={`edit-btn-${r.registration_id}`}><Pencil size={15} /></button>
                    <button onClick={() => setDeleting(r)} className="row-btn danger" title="Delete" data-testid={`delete-btn-${r.registration_id}`}><Trash2 size={15} /></button>
                  </div>
                </td>
                <td>{rows.length - rows.indexOf(r)}</td><td className="font-bold text-orange-700">{r.registration_id}</td><td>{r.full_name}</td><td>{r.father_name}</td>
                <td>{r.mobile}</td><td>{r.parent_mobile}</td><td>{r.dob.split("-").reverse().join("/")}</td>
                <td>{r.age_on_event_day}</td><td>{r.player_class}</td><td>{r.school}</td><td>{evShort(r.events)}</td><td>{r.emergency_contact}</td>
              </tr>
            ))}
            {rows?.length === 0 && <tr><td colSpan={13} className="text-center py-10 text-slate-500">अभी कोई पंजीकरण नहीं · No registrations yet</td></tr>}
          </tbody>
        </table>
      </div>
      <BulkUpload open={bulkOpen} auth={auth} onClose={() => setBulkOpen(false)} onDone={load} />
      <EditDialog row={editing} auth={auth} onClose={() => setEditing(null)}
        onSaved={(d) => { setRows((p) => p.map((x) => (x.registration_id === d.registration_id ? d : x))); setEditing(null); }} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-white" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-hi">रिकॉर्ड हटाएँ? · Delete record?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.full_name} ({deleting?.registration_id}) का पंजीकरण हमेशा के लिए हट जाएगा। This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">रद्द · Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">हटाएँ · Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
